import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  updateDoc, 
  doc, 
  Timestamp,
  serverTimestamp,
  FieldValue
} from 'firebase/firestore';
import { db } from '@/utils/firebase';
import { v4 as uuidv4 } from 'uuid';
import { sendInvitationEmail } from '@/utils/email/emailService';
import { MemberPermission, MemberRole, MemberStatus, Invitation, EntityType } from '@/types/schema';

// Constants
const INVITATIONS_COLLECTION = 'invitations';
const PORTFOLIOS_COLLECTION = 'portfolios';
const BUSINESSES_COLLECTION = 'businesses';

/**
 * Create a new invitation
 */
export const createInvitation = async (
  email: string,
  firstName: string,
  lastName: string,
  phone: string | undefined,
  entityId: string,
  entityType: EntityType,
  role: MemberRole,
  permissions: MemberPermission[],
  invitedBy: string,
  invitedByName: string
): Promise<string> => {
  try {
    // Check if an active invitation already exists
    const existingInvitations = await getInvitationsForEmail(email, entityId, entityType);
    
    if (existingInvitations.length > 0) {
      console.log(`Invitation already exists for ${email} to ${entityType} ${entityId}`);
      return existingInvitations[0].id;
    }
    
    // Create token for the invitation
    const token = uuidv4();
    
    // Set expiration date (30 days from now)
    const now = new Date();
    const expiresAt = new Date(now);
    expiresAt.setDate(now.getDate() + 30);
    
    // Create invitation document
    const invitation: Omit<Invitation, 'id'> = {
      email,
      entityId,
      entityType,
      role,
      permissions,
      status: 'pending',
      invitedBy,
      invitedByName,
      invitedAt: now, // Use Date object, will be converted to Timestamp
      expiresAt, // Use Date object, will be converted to Timestamp
      firstName,
      lastName,
      phone,
      token
    };
    
    // Add to Firestore
    const docRef = await addDoc(collection(db, INVITATIONS_COLLECTION), invitation);
    console.log(`Created invitation with ID: ${docRef.id}`);
    
    // Generate invitation link
    const inviteLink = `${window.location.origin}/invitation-accept?token=${token}`;
    
    // Send email
    await sendInvitationEmail({ ...invitation, id: docRef.id } as Invitation, inviteLink);
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating invitation:', error);
    throw error;
  }
};

/**
 * Get all invitations for a specific email address
 */
export const getInvitationsForEmail = async (
  email: string,
  entityId?: string,
  entityType?: EntityType
): Promise<Invitation[]> => {
  try {
    let q = query(collection(db, INVITATIONS_COLLECTION), where('email', '==', email));
    
    // Add entity filters if provided
    if (entityId && entityType) {
      q = query(q, where('entityId', '==', entityId), where('entityType', '==', entityType));
    }
    
    const querySnapshot = await getDocs(q);
    
    const invitations: Invitation[] = [];
    querySnapshot.forEach((doc) => {
      invitations.push({ id: doc.id, ...doc.data() } as Invitation);
    });
    
    return invitations;
  } catch (error) {
    console.error('Error getting invitations for email:', error);
    throw error;
  }
};

/**
 * Get all pending invitations for a specific email address
 */
export const getPendingInvitationsForEmail = async (email: string): Promise<Invitation[]> => {
  try {
    const q = query(
      collection(db, INVITATIONS_COLLECTION),
      where('email', '==', email),
      where('status', '==', 'pending')
    );
    
    const querySnapshot = await getDocs(q);
    
    const invitations: Invitation[] = [];
    querySnapshot.forEach((doc) => {
      invitations.push({ id: doc.id, ...doc.data() } as Invitation);
    });
    
    return invitations;
  } catch (error) {
    console.error('Error getting pending invitations:', error);
    throw error;
  }
};

/**
 * Get all invitations for an entity
 */
export const getInvitationsForEntity = async (
  entityId: string,
  entityType: EntityType
): Promise<Invitation[]> => {
  try {
    const q = query(
      collection(db, INVITATIONS_COLLECTION),
      where('entityId', '==', entityId),
      where('entityType', '==', entityType)
    );
    
    const querySnapshot = await getDocs(q);
    
    const invitations: Invitation[] = [];
    querySnapshot.forEach((doc) => {
      invitations.push({ id: doc.id, ...doc.data() } as Invitation);
    });
    
    return invitations;
  } catch (error) {
    console.error('Error getting entity invitations:', error);
    throw error;
  }
};

/**
 * Accept an invitation
 */
export const acceptInvitation = async (invitationId: string, userId: string): Promise<void> => {
  try {
    const invitationRef = doc(db, INVITATIONS_COLLECTION, invitationId);
    
    // Update invitation status
    await updateDoc(invitationRef, {
      status: 'accepted',
      acceptedAt: serverTimestamp(),
      userId
    });
    
    // Get the updated invitation data
    const invitationSnapshot = await getDocs(query(
      collection(db, INVITATIONS_COLLECTION),
      where('__name__', '==', invitationId)
    ));
    
    if (invitationSnapshot.empty) {
      throw new Error('Invitation not found after update');
    }
    
    const invitation = { id: invitationSnapshot.docs[0].id, ...invitationSnapshot.docs[0].data() } as Invitation;
    
    // Update the entity with the new member
    const collectionName = invitation.entityType === 'portfolio' ? PORTFOLIOS_COLLECTION : BUSINESSES_COLLECTION;
    const entityRef = doc(db, collectionName, invitation.entityId);
    
    // Add member to entity's members array
    // Note: This is a simplified update. In a production app, you'd use a transaction
    // and more carefully update nested arrays to avoid race conditions
    
    // Create a new member object from invitation data
    const newMember = {
      userId,
      role: invitation.role,
      permissions: invitation.permissions,
      status: 'active' as MemberStatus,
      firstName: invitation.firstName,
      lastName: invitation.lastName,
      email: invitation.email,
      phone: invitation.phone,
      joinedAt: serverTimestamp(),
      invitedAt: invitation.invitedAt,
      invitedBy: invitation.invitedBy,
      isCurrentUser: false
    };
    
    // We need to use dot notation for updating array fields
    await updateDoc(entityRef, {
      [`members`]: serverTimestamp() // This field will be updated by a Cloud Function
      // Alternatively, you could use arrayUnion or a custom update strategy
    });
    
    console.log(`Accepted invitation ${invitationId} for user ${userId}`);
  } catch (error) {
    console.error('Error accepting invitation:', error);
    throw error;
  }
};

/**
 * Decline an invitation
 */
export const declineInvitation = async (invitationId: string): Promise<void> => {
  try {
    const invitationRef = doc(db, INVITATIONS_COLLECTION, invitationId);
    
    await updateDoc(invitationRef, {
      status: 'declined',
      declinedAt: serverTimestamp()
    });
    
    console.log(`Declined invitation ${invitationId}`);
  } catch (error) {
    console.error('Error declining invitation:', error);
    throw error;
  }
};

/**
 * Cancel an invitation
 */
export const cancelInvitation = async (invitationId: string): Promise<void> => {
  try {
    const invitationRef = doc(db, INVITATIONS_COLLECTION, invitationId);
    
    await updateDoc(invitationRef, {
      status: 'cancelled',
      cancelledAt: serverTimestamp()
    });
    
    console.log(`Cancelled invitation ${invitationId}`);
  } catch (error) {
    console.error('Error cancelling invitation:', error);
    throw error;
  }
};

/**
 * Get invitation by token
 */
export const getInvitationByToken = async (token: string): Promise<Invitation | null> => {
  try {
    const q = query(collection(db, INVITATIONS_COLLECTION), where('token', '==', token));
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    return { id: querySnapshot.docs[0].id, ...querySnapshot.docs[0].data() } as Invitation;
  } catch (error) {
    console.error('Error getting invitation by token:', error);
    throw error;
  }
};
